import api from '../api/axios';

const getAllProperties = (skip = 0, take = 1000000, withDeleted) => {
  const response = api.get(
    `/property?skip=${skip}&take=${take}&sort=deletedAt%2Cdesc&sort=id%2Casc&withDeleted=${withDeleted}`,
  );

  return response;
};

const getPropertyById = (id) => {
  const response = api.get(`/property/${id}`);

  return response;
};

const create = (property) => {
  const response = api.post('/property', property);

  return response;
};

const update = (id, updatedProperty) => {
  const response = api.patch(`/property/${id}`, updatedProperty);

  return response;
};

const remove = (id) => {
  const response = api.delete(`/property/${id}`);

  return response;
};

const restore = (id) => {
  const response = api.get(`/property/${id}/restore`);

  return response;
};

const searchRequest = (skip = 0, take = 1000000, query, withDeleted = true, options = {}) => {
  const response = api.get(
    `/property?skip=${skip}&take=${take}&sort=deletedAt%2Cdesc&query=${query.replace(
      /#/g,
      '%23',
    )}&withDeleted=${withDeleted}`,
    { ...options },
  );

  return response;
};

const getPropertyTypes = (widthDeleted = false) => {
  const response = api.get(`/propertyType?take=1000000&skip=0&sort=deletedAt%2Cdesc&withDeleted=${widthDeleted}`);

  return response;
};

const getPropertyTypeById = (id) => {
  const response = api.get(`/propertyType/${id}`);

  return response;
};

const createPropertyType = (name) => {
  const response = api.post(`/propertyType`, {
    name,
  });

  return response;
};

const removePropertyType = (id) => {
  const response = api.delete(`/propertyType/${id}`);

  return response;
};

const restorePropertyType = (id) => {
  const response = api.get(`/propertyType/${id}/restore`);

  return response;
};

const searchPropertyType = (skip = 0, take = 1000000, query, withDeleted = true) => {
  const response = api.get(
    `/propertyType?skip=${skip}&take=${take}&sort=deletedAt%2Cdesc&query=${query}&withDeleted=${withDeleted}`,
  );

  return response;
};

const getInvalidProperties = (skip = 0, take = 1000000) => {
  const response = api.get(`/property?skip=${skip}&take=${take}&sort=deletedAt%2Cdesc&onlyUndetectedAddresses=true`);

  return response;
};

/**
 * Check if a property with the same address already exists
 * Used to prevent duplicate property creation
 * @param {Object} addressData - Object with streetNumber, streetName, city, state, zipCode, unit
 * @returns {Promise} Response with isDuplicate boolean and existingProperty if found
 */
const checkDuplicate = (addressData) => {
  const response = api.post('/property/check-duplicate', addressData);

  return response;
};

/**
 * Update legacy proposal status
 * @param {string} propertyId - Property UUID
 * @param {string} proposalNumber - Legacy proposal number (e.g. G16092901)
 * @param {string} status - New status (Active, Pending, Completed, Cold, Cancelled)
 */
/**
 * Add note to property
 * @param {string} propertyId - Property UUID
 * @param {string} note - Note text
 */
const addPropertyNote = (propertyId, note) => {
  const response = api.post(`/property/${propertyId}/notes`, { note });

  return response;
};

const updateLegacyProposalStatus = (propertyId, proposalNumber, status) => {
  const response = api.patch(`/property/${propertyId}/legacy-proposal-status`, {
    proposalNumber,
    status,
  });

  return response;
};

/**
 * List CompanyCam projects linked to this property
 * @param {string} propertyId
 */
const listCcProjects = (propertyId) => api.get(`/property/${propertyId}/companycam/projects`);

/**
 * Pull all new photos from CompanyCam into the property's media gallery.
 * Idempotent — already-imported photos are skipped via cc_sync_log.
 * Useful when a CompanyCam user moved/merged photos between projects
 * (webhook delivers photo.updated for that, but this is the manual fallback).
 * @param {string} propertyId
 */
const pullCcPhotos = (propertyId) => api.post(`/property/${propertyId}/companycam/pull-photos`);

/**
 * Manually attach an existing CompanyCam project to this property.
 * @param {string} propertyId
 * @param {{ ccProjectId?: string, ccProjectUrl?: string }} payload
 */
const attachCcProject = (propertyId, payload) => api.post(`/property/${propertyId}/companycam/projects`, payload);

/**
 * Detach a CompanyCam project mapping (already-imported photos remain in gallery).
 * @param {string} propertyId
 * @param {string} ccProjectId
 */
const detachCcProject = (propertyId, ccProjectId) => api.delete(`/property/${propertyId}/companycam/projects/${ccProjectId}`);

const propertiesServices = {
  getAllProperties,
  getPropertyById,
  create,
  update,
  remove,
  restore,
  searchRequest,
  getPropertyTypes,
  getPropertyTypeById,
  createPropertyType,
  removePropertyType,
  restorePropertyType,
  searchPropertyType,
  getInvalidProperties,
  checkDuplicate,
  addPropertyNote,
  updateLegacyProposalStatus,
  listCcProjects,
  pullCcPhotos,
  attachCcProject,
  detachCcProject,
};

export default propertiesServices;
